import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';
import { User, roleLabel, managedSellers } from '../../types';
import SellerFormModal from '../../components/SellerFormModal';
import Pagination, { usePagination } from '../../components/Pagination';
import { useAuth } from '../../contexts/AuthContext';

// Último acesso pode vir como Timestamp do Firestore ou Date — formata os dois. Blindado contra
// valor forjado (só chama toDate se for função de verdade) para não quebrar a lista.
const fmtAcesso = (v: any): string => {
  const d = typeof v?.toDate === 'function' ? v.toDate() : (v instanceof Date ? v : null);
  if (!(d instanceof Date) || isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};
// Compara versões "1.10.0" vs "1.9.0" (semver simples). >0 se a>b.
const cmpVer = (a?: string, b?: string): number => {
  const pa = String(a || '').split('.').map(n => parseInt(n, 10) || 0);
  const pb = String(b || '').split('.').map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) { const d = (pa[i] || 0) - (pb[i] || 0); if (d) return d; }
  return 0;
};

export default function SellersPage() {
  const { user, isAdmin } = useAuth(); // isAdmin = acesso total
  const [sellers, setSellers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [importingId, setImportingId] = useState<string | null>(null);

  const load = async () => {
    const q = query(collection(db, 'users'), orderBy('name'));
    const snap = await getDocs(q);
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() })) as User[];
    // Acesso total vê todos os usuários; gestor vê só os vendedores da hierarquia dele.
    setSellers(isAdmin ? all : managedSellers(user, all));
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, user]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (seller: User) => { setEditing(seller); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); load(); };

  // Via Cloud Function: além de gravar `active`, DESABILITA a conta no Firebase Auth e revoga os
  // tokens. Gravar só o campo no Firestore não revogava nada — a credencial seguia válida.
  const toggleActive = async (seller: User) => {
    const novo = !seller.active;
    try {
      await httpsCallable(functions, 'definirAtivoUsuario')({ uid: seller.id, active: novo });
      setSellers(prev => prev.map(s => s.id === seller.id ? { ...s, active: novo } : s));
    } catch (e: any) {
      alert(e?.message || 'Não foi possível alterar a situação do usuário.');
    }
  };

  const handleDelete = async (seller: User) => {
    const ok = window.confirm(
      `Excluir o vendedor "${seller.name}"?\n\nA conta de login e o perfil serão removidos por completo. Os check-ins já registrados serão mantidos no histórico.`
    );
    if (!ok) return;
    try {
      const excluir = httpsCallable(functions, 'excluirVendedor');
      await excluir({ uid: seller.id });
      setSellers(prev => prev.filter(s => s.id !== seller.id));
    } catch (e: any) {
      window.alert(e?.message || 'Não foi possível excluir o vendedor.');
    }
  };

  const handleImport = async (seller: User) => {
    if (!seller.codVendSankhya) {
      window.alert('Este vendedor não tem CODVEND cadastrado. Edite o vendedor e preencha o Código do Vendedor no Sankhya.');
      return;
    }
    const ok = window.confirm(
      `Importar a carteira de clientes de "${seller.name}" (CODVEND ${seller.codVendSankhya}) do Sankhya?\n\nOs clientes vinculados a este vendedor serão trazidos para o app. Clientes já existentes apenas serão vinculados (sem duplicar).`
    );
    if (!ok) return;
    setImportingId(seller.id);
    try {
      const importar = httpsCallable(functions, 'importarCarteiraSankhya');
      const res: any = await importar({ sellerId: seller.id });
      const d = res?.data || {};
      const diag = d.diag ? `\n\n[Diagnóstico endereço]\n${JSON.stringify(d.diag, null, 2)}` : '';
      window.alert(
        `Importação concluída!\n\nParceiros encontrados: ${d.total ?? 0}\nNovos clientes criados: ${d.criados ?? 0}\nClientes já existentes vinculados: ${d.atualizados ?? 0}${diag}`
      );
    } catch (e: any) {
      window.alert(e?.message || 'Não foi possível importar a carteira.');
    } finally {
      setImportingId(null);
    }
  };

  const codvendNum = (s: User) => {
    const n = Number(s.codVendSankhya);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY; // sem CODVEND vai para o fim
  };

  // "Desatualizado" = abaixo da MAIOR versão vista entre os usuários (sem depender de constante).
  const versaoMaisNova = sellers.reduce((m, s) => (s.appVersion && cmpVer(s.appVersion, m) > 0 ? s.appVersion : m), '');

  const filtered = sellers
    .filter(s => {
      const q = search.toLowerCase();
      const matchesSearch =
        s.name.toLowerCase().includes(q) ||
        String(s.codVendSankhya || '').includes(q) ||
        (s.email || '').toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && s.active) ||
        (statusFilter === 'inactive' && !s.active);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => codvendNum(a) - codvendNum(b));

  // Paginação (25 por página por padrão) — a lista de usuários cresceu com as equipes
  const pager = usePagination(filtered, `${search}|${statusFilter}`);

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.title}>{isAdmin ? 'Usuários' : 'Vendedores'}</h1>
          <p style={styles.subtitle}>{sellers.filter(s => s.active).length} ativo(s)</p>
        </div>
        {isAdmin && (
          <button style={styles.addBtn} onClick={openNew}>
            + Novo Usuário
          </button>
        )}
      </div>

      <div style={styles.filters}>
        <input
          style={styles.search}
          placeholder="Buscar por nome, código ou e-mail..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select style={styles.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}>
          <option value="all">Todos</option>
          <option value="active">Ativos</option>
          <option value="inactive">Inativos</option>
        </select>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <div style={styles.grid}>
          {pager.pageItems.map(seller => (
            <div key={seller.id} style={styles.card}>
              <div style={styles.cardAvatar}>
                {seller.name.charAt(0).toUpperCase()}
              </div>
              <div style={styles.cardInfo}>
                <div style={styles.cardName}>
                  {seller.codVendSankhya ? `${seller.codVendSankhya} - ${seller.name}` : seller.name}
                </div>
                <div style={styles.cardEmail}>{seller.email}</div>
                {seller.phone && <div style={styles.cardPhone}>{seller.phone}</div>}
                {!seller.codVendSankhya && <div style={styles.noCod}>⚠️ Sem CODVEND Sankhya</div>}
                <div style={styles.cardMeta}>
                  📱 {seller.appVersion ? `v${seller.appVersion}` : 'versão desconhecida'}
                  {seller.appVersion && cmpVer(seller.appVersion, versaoMaisNova) < 0 && <span style={styles.outdated}> (desatualizado)</span>}
                  {seller.ultimoAcesso && ` · último acesso ${fmtAcesso(seller.ultimoAcesso)}`}
                </div>
              </div>
              <div style={styles.cardActions}>
                <span style={{ ...styles.badge, ...(seller.active ? styles.badgeActive : styles.badgeInactive) }}>
                  {seller.active ? 'Ativo' : 'Inativo'}
                </span>
                <span style={{ ...styles.badge, ...styles.roleBadge }}>{roleLabel(seller.role)}</span>
                {isAdmin && <button style={styles.actionBtn} onClick={() => openEdit(seller)}>Editar</button>}
                {isAdmin && (
                  <button style={styles.actionBtn} onClick={() => toggleActive(seller)}>
                    {seller.active ? 'Desativar' : 'Ativar'}
                  </button>
                )}
                {isAdmin && seller.role === 'seller' && (
                  <button
                    style={{ ...styles.actionBtn, ...(seller.codVendSankhya ? styles.importBtn : styles.importBtnDisabled) }}
                    onClick={() => handleImport(seller)}
                    disabled={importingId === seller.id || !seller.codVendSankhya}
                    title={seller.codVendSankhya ? 'Importar carteira de clientes do Sankhya' : 'Cadastre o CODVEND para importar'}
                  >
                    {importingId === seller.id ? 'Importando...' : '↓ Importar carteira'}
                  </button>
                )}
                {isAdmin && <button style={styles.deleteBtn} onClick={() => handleDelete(seller)}>Excluir</button>}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p style={{ color: '#999' }}>Nenhum vendedor encontrado.</p>}
        </div>
      )}

      {!loading && <Pagination pager={pager} label="usuários" />}

      {modalOpen && (
        <SellerFormModal
          seller={editing}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14 },
  addBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  filters: { display: 'flex', gap: 12, marginBottom: 20 },
  search: { flex: 1, border: '1px solid #e0e0e0', borderRadius: 10, padding: '10px 14px', fontSize: 15, background: '#fff' },
  select: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, background: '#fff', cursor: 'pointer' },
  noCod: { fontSize: 11, color: '#d97706', marginTop: 4, fontWeight: 600 },
  cardMeta: { fontSize: 11, color: '#94a3b8', marginTop: 4 },
  outdated: { color: '#d97706', fontWeight: 700 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  card: { background: '#fff', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  cardAvatar: { width: 48, height: 48, borderRadius: 24, background: '#f5821f', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700 },
  cardInfo: { flex: 1 },
  cardName: { fontWeight: 700, fontSize: 16, color: '#1a1a2e' },
  cardEmail: { fontSize: 13, color: '#666', marginTop: 2 },
  cardPhone: { fontSize: 13, color: '#f5821f', marginTop: 2 },
  cardActions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  deleteBtn: { background: 'none', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#dc2626' },
  badge: { padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  badgeActive: { background: '#dcfce7', color: '#16a34a' },
  badgeInactive: { background: '#f3f4f6', color: '#6b7280' },
  roleBadge: { background: '#eef2ff', color: '#4338ca' },
  actionBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#555' },
  importBtn: { border: '1px solid #fed7aa', color: '#f5821f', background: '#fff7ed' },
  importBtnDisabled: { border: '1px solid #e5e7eb', color: '#9ca3af', background: '#f9fafb', cursor: 'not-allowed' },
};
